import {useCallback, useEffect, useRef, useState} from 'react';

import {getSsoAuditLog} from 'src/openapi/admin/apis/SsoAuditApi';
import {SsoAuditRecord} from 'src/openapi/admin/models/SsoAuditRecord';
import {useAppDispatch} from 'src/store/hooks';

export type UseAuditLogReturn = {
    entries: SsoAuditRecord[],
    loading: boolean,
    error: string | null,
    actionType: string | undefined,
    onActionTypeChange: (actionType: string | undefined) => void,
};

const PAGE_SIZE = 25;

export function useAuditLog(): UseAuditLogReturn {
    const dispatch = useAppDispatch();
    const [entries, setEntries] = useState<SsoAuditRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [actionType, setActionType] = useState<string | undefined>(undefined);
    const logAbortRef = useRef<AbortController | null>(null);

    useEffect(() => {
        logAbortRef.current?.abort();
        const controller = new AbortController();
        logAbortRef.current = controller;
        setLoading(true);
        setError(null);

        (async () => {
            try {
                const response = await dispatch(
                    getSsoAuditLog(
                        {auditLogRequest: {page: 0, pageSize: PAGE_SIZE, actionType}},
                        {signal: controller.signal},
                    ),
                );

                if (controller.signal.aborted) {
                    return;
                }

                if (response.payload?.success) {
                    setEntries(response.payload.data.items);
                } else {
                    setError('Failed to load audit log. Please refresh and try again.');
                }
            } catch (err) {
                if ((err as {name?: string})?.name === 'AbortError') {
                    return;
                }
                setError('Failed to load audit log. Please refresh and try again.');
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
                if (logAbortRef.current === controller) {
                    logAbortRef.current = null;
                }
            }
        })();

        return () => {
            controller.abort();
        };
    }, [dispatch, actionType]);

    const onActionTypeChange = useCallback((type: string | undefined) => {
        setActionType(type);
    }, []);

    return {
        entries,
        loading,
        error,
        actionType,
        onActionTypeChange,
    };
}
